import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import api from '../../api/client';

const STAFF_ROLES = ['ROOT', 'ADMIN', 'OPERATORE'];

const fmtLabel = (ymd) => {
  const d = new Date(ymd + 'T12:00:00');
  const giorni = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
  const mesi = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
  return `${giorni[d.getDay()]} ${d.getDate()} ${mesi[d.getMonth()]}`;
};
const fmtDataEstesa = (ymd) => {
  const d = new Date(ymd + 'T12:00:00');
  return d.toLocaleDateString('it-IT', { weekday: 'long', day: '2-digit', month: 'long' });
};
const oraFine = (ora_inizio, durata) => {
  const [h, m] = ora_inizio.split(':').map(Number);
  const tot = h * 60 + m + durata;
  return `${String(Math.floor(tot / 60)).padStart(2, '0')}:${String(tot % 60).padStart(2, '0')}`;
};

const s = {
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '18px' },
  navBar: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
  navBtn: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text)', flexShrink: 0 },
  weekLabel: { flex: 1, textAlign: 'center', fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight: 800, letterSpacing: '1px', color: 'var(--text)' },
  tabBar: { display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '1px solid var(--border)' },
  tab: { padding: '9px 16px', background: 'transparent', border: 'none', borderBottom: '2px solid transparent', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 700, letterSpacing: '1px', color: '#666' },
  tabActive: { color: '#F4600C', borderBottomColor: '#F4600C' },
  legenda: { display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '14px', fontSize: 'var(--font-size-xs)', color: '#888' },
  legDot: (bg, border) => ({ width: '11px', height: '11px', borderRadius: '3px', background: bg, border: `1px solid ${border || bg}`, display: 'inline-block', marginRight: '5px', verticalAlign: 'middle' }),
  sedeTitle: { fontFamily: "'Barlow Condensed'", fontWeight: 800, letterSpacing: '1px', color: '#F4600C', fontSize: 'var(--font-size-md)', margin: '18px 0 8px', display: 'flex', alignItems: 'center', gap: '6px' },
  campoTitle: { fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--text)', margin: '10px 0 4px' },
  table: { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', marginBottom: '8px' },
  th: { fontSize: 'var(--font-size-xs)', color: '#666', fontWeight: 700, padding: '4px 2px', textAlign: 'center' },
  rowLabel: { fontSize: 'var(--font-size-xs)', color: '#999', fontWeight: 600, padding: '4px 6px', whiteSpace: 'nowrap', width: '78px' },
  cellTd: { padding: '2px', textAlign: 'center' },
  cellBase: { display: 'block', width: '100%', borderRadius: '6px', padding: '6px 2px', fontSize: '11px', fontWeight: 700, cursor: 'default', border: '1.5px solid transparent', lineHeight: 1.25 },
};

// dataRif/onDataRifChange: se passati, il componente diventa "controllato" (usato dallo staff per
// saltare a una settimana specifica dalla panoramica mensile). Se omessi, gestisce da solo la propria
// settimana (uso normale nel portale cliente).
export default function CalendarioCampi({ dataRif: dataRifProp, onDataRifChange }) {
  const { user } = useAuth();
  const isStaff = STAFF_ROLES.includes(user?.ruolo);
  const [dataRifState, setDataRifState] = useState(new Date().toISOString().slice(0, 10));
  const dataRif = dataRifProp ?? dataRifState;
  const setDataRif = onDataRifChange ?? setDataRifState;

  const [cal, setCal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('cal'); // cal | mie
  const [selezione, setSelezione] = useState(null); // {slot, sedeNome, campoNome, data}
  const [bloccoAttivo, setBloccoAttivo] = useState(false);
  const [bloccoNota, setBloccoNota] = useState('');
  const [dettaglio, setDettaglio] = useState(null); // cella occupata/bloccata cliccata dallo staff
  const [modalita, setModalita] = useState([]);
  const [form, setForm] = useState({ nome: user?.nome || '', email: user?.email || '', telefono: '', modalita_pagamento_id: '' });
  const [step, setStep] = useState('cal'); // cal | form | ok
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [okData, setOkData] = useState(null);
  const [miePrenotazioni, setMiePrenotazioni] = useState([]);
  const [miePrenLoading, setMiePrenLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/campi/calendario', { params: { data: dataRif } });
      setCal(r.data);
    } catch { setCal(null); }
    finally { setLoading(false); }
  }, [dataRif]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/campi/modalita-pagamento').then(r => setModalita(r.data.filter(m => m.attiva))).catch(() => {}); }, []);

  const loadMie = async () => {
    setMiePrenLoading(true);
    try { const r = await api.get('/campi/mie-prenotazioni'); setMiePrenotazioni(r.data); }
    catch { setMiePrenotazioni([]); }
    finally { setMiePrenLoading(false); }
  };
  useEffect(() => { if (tab === 'mie') loadMie(); }, [tab]);

  const resetSelezione = () => { setSelezione(null); setBloccoAttivo(false); setBloccoNota(''); setErr(''); };

  const navSett = (dir) => {
    const d = new Date(dataRif + 'T12:00:00');
    d.setDate(d.getDate() + dir * 7);
    setDataRif(d.toISOString().slice(0, 10));
    resetSelezione();
  };

  const clickCella = (sede, campo, slot, g) => {
    if (g.disponibile) {
      setDettaglio(null);
      resetSelezione();
      setSelezione({ slot, sedeNome: sede.nome, campoNome: campo.nome, data: g.data, prezzo: slot.prezzo, ora_inizio: slot.ora_inizio, ora_fine: oraFine(slot.ora_inizio, slot.durata_minuti) });
      return;
    }
    if (isStaff && g.prenotazione_id) {
      resetSelezione();
      setDettaglio({
        id: g.prenotazione_id, stato: g.stato, nome: g.nome, email: g.email, telefono: g.telefono,
        codice: g.codice, prezzo: g.prezzo_prenotazione, pagato: g.pagato, note: g.note,
        sedeNome: sede.nome, campoNome: campo.nome, data: g.data, ora_inizio: slot.ora_inizio, ora_fine: oraFine(slot.ora_inizio, slot.durata_minuti),
      });
    }
  };

  const togglePagatoDettaglio = async () => {
    try { await api.patch(`/campi/${dettaglio.id}/pagato`, { pagato: !dettaglio.pagato }); setDettaglio(d => ({ ...d, pagato: !d.pagato })); load(); }
    catch (e) { alert(e.response?.data?.error || 'Errore'); }
  };
  const annullaDettaglio = async () => {
    if (!window.confirm('Annullare questa prenotazione?')) return;
    try { await api.post(`/campi/${dettaglio.id}/annulla`); setDettaglio(null); load(); }
    catch (e) { alert(e.response?.data?.error || 'Errore annullamento'); }
  };
  const sbloccaDettaglio = async () => {
    try { await api.delete(`/campi/blocco/${dettaglio.id}`); setDettaglio(null); load(); }
    catch (e) { alert(e.response?.data?.error || 'Errore'); }
  };

  const vaiAlForm = () => { if (selezione) { setStep('form'); setForm(f => ({ ...f, nome: user?.nome || f.nome, email: user?.email || f.email })); } };

  const confermaBlocco = async () => {
    if (!selezione) return;
    setSaving(true); setErr('');
    try {
      await api.post('/campi/blocca', { slot_id: selezione.slot.id, data: selezione.data, note: bloccoNota });
      resetSelezione();
      load();
    } catch (e) { setErr(e.response?.data?.error || 'Errore. Riprova.'); }
    finally { setSaving(false); }
  };

  const confermaPrenota = async () => {
    if (!form.nome.trim() || !form.email.trim()) { setErr('Inserisci nome ed email.'); return; }
    setSaving(true); setErr('');
    try {
      const r = await api.post('/campi/prenota', {
        slot_id: selezione.slot.id, data: selezione.data,
        nome: form.nome, email: form.email, telefono: form.telefono,
        modalita_pagamento_id: form.modalita_pagamento_id || undefined,
      });
      setOkData(r.data);
      setStep('ok');
      load();
    } catch (e) { setErr(e.response?.data?.error || 'Errore. Riprova.'); }
    finally { setSaving(false); }
  };

  const nuovaPrenotazione = () => {
    resetSelezione(); setStep('cal'); setOkData(null);
    setForm({ nome: user?.nome || '', email: user?.email || '', telefono: '', modalita_pagamento_id: '' });
  };

  const annulla = async (id) => {
    if (!window.confirm('Annullare questa prenotazione?')) return;
    try {
      await api.post(`/campi/${id}/annulla`);
      loadMie(); load();
    } catch (e) {
      const d = e.response?.data;
      alert(d?.scaduto ? d.error : (d?.error || 'Errore annullamento'));
    }
  };

  const inp = { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '9px 12px', color: 'var(--text)', fontSize: 'var(--font-size-sm)', outline: 'none', width: '100%', boxSizing: 'border-box' };
  const lab = { fontSize: 'var(--font-size-xs)', letterSpacing: '2px', fontWeight: 700, color: '#555', display: 'block', marginBottom: '4px' };

  if (step === 'form' && selezione) {
    return (
      <div style={s.card}>
        <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight: 800, color: '#F4600C', letterSpacing: '1px', marginBottom: '14px' }}>I TUOI DATI</div>
        <div style={{ background: 'var(--bg-input)', borderLeft: '3px solid #F4600C', borderRadius: '0 8px 8px 0', padding: '10px 14px', marginBottom: '16px', fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
          <strong>{selezione.sedeNome} — {selezione.campoNome}</strong><br/>
          {fmtDataEstesa(selezione.data)} · {selezione.ora_inizio}–{selezione.ora_fine} · € {selezione.prezzo.toFixed(2)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
          <div><label style={lab}>NOME E COGNOME *</label><input style={inp} value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}/></div>
          <div><label style={lab}>EMAIL *</label><input style={inp} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}/></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <div><label style={lab}>TELEFONO</label><input style={inp} value={form.telefono} onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))}/></div>
          <div>
            <label style={lab}>MODALITÀ DI PAGAMENTO</label>
            <select style={inp} value={form.modalita_pagamento_id} onChange={e => setForm(f => ({ ...f, modalita_pagamento_id: e.target.value }))}>
              <option value="">Da definire</option>
              {modalita.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
            </select>
          </div>
        </div>
        {err && <div style={{ background: '#EF444420', border: '1px solid #EF444440', color: '#f1948a', borderRadius: '8px', padding: '8px 12px', marginBottom: '12px', fontSize: 'var(--font-size-sm)' }}>{err}</div>}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => setStep('cal')} style={{ background: 'transparent', border: '1px solid var(--border2)', borderRadius: '8px', color: '#888', padding: '10px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 'var(--font-size-sm)' }}>← Indietro</button>
          <button onClick={confermaPrenota} disabled={saving} style={{ flex: 1, background: '#F4600C', border: 'none', borderRadius: '8px', color: '#fff', padding: '10px 18px', cursor: 'pointer', fontWeight: 800, fontSize: 'var(--font-size-sm)', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Invio...' : 'Prenota ora'}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'ok' && okData) {
    return (
      <div style={s.card}>
        <div style={{ textAlign: 'center', padding: '10px 0 20px' }}>
          <div style={{ fontSize: '2.4rem', marginBottom: '6px' }}>🎉</div>
          <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-xl)', fontWeight: 900, color: '#F4600C', letterSpacing: '1px' }}>PRENOTAZIONE CONFERMATA</div>
          <div style={{ color: '#888', fontSize: 'var(--font-size-sm)', margin: '4px 0 16px' }}>Riceverai una email di conferma. Conserva il codice.</div>
          <div style={{ display: 'inline-block', background: 'var(--bg-input)', border: '2px dashed #F4600C', borderRadius: '8px', padding: '10px 28px', fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-xl)', fontWeight: 900, letterSpacing: '4px', color: 'var(--text)', marginBottom: '18px' }}>
            {okData.codice}
          </div>
          <div style={{ background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '14px', textAlign: 'left', maxWidth: '440px', margin: '0 auto 16px' }}>
            <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 800, letterSpacing: '1px', color: '#F4600C', marginBottom: '10px' }}>💳 PAGAMENTO</div>
            {[['Importo', `€ ${okData.prezzo.toFixed(2)}`], ['Causale', `Affitto campo ${okData.nome} ${okData.codice}`]].map(([l, v]) => (
              <div key={l} style={{ padding: '6px 0', borderBottom: '1px solid #222' }}>
                <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase', letterSpacing: '1px' }}>{l}</div>
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)', fontWeight: 600, wordBreak: 'break-word' }}>{v}</div>
              </div>
            ))}
            {okData.istruzioniPagamento && (
              <div style={{ padding: '10px 0 0', fontSize: 'var(--font-size-sm)', color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{okData.istruzioniPagamento}</div>
            )}
          </div>
          {okData.messaggioConferma && (
            <div style={{ fontSize: 'var(--font-size-sm)', color: '#aaa', maxWidth: '440px', margin: '0 auto 16px', whiteSpace: 'pre-wrap' }}>{okData.messaggioConferma}</div>
          )}
          <button onClick={nuovaPrenotazione} style={{ background: '#F4600C', border: 'none', borderRadius: '8px', color: '#fff', padding: '11px 26px', cursor: 'pointer', fontWeight: 800, fontSize: 'var(--font-size-sm)' }}>Nuova prenotazione</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={s.tabBar}>
        <button style={{ ...s.tab, ...(tab === 'cal' ? s.tabActive : {}) }} onClick={() => setTab('cal')}>CALENDARIO</button>
        <button style={{ ...s.tab, ...(tab === 'mie' ? s.tabActive : {}) }} onClick={() => setTab('mie')}>LE MIE PRENOTAZIONI</button>
      </div>

      {tab === 'mie' && (
        <div>
          {miePrenLoading && <div style={{ color: '#666', padding: '20px' }}>Caricamento...</div>}
          {!miePrenLoading && miePrenotazioni.length === 0 && <div style={{ color: '#666', padding: '20px' }}>Nessuna prenotazione.</div>}
          {miePrenotazioni.map(p => (
            <div key={p.id} style={{ ...s.card, marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: 'var(--font-size-sm)' }}>{p.campo?.sede?.nome || p.sede_nome} — {p.campo?.nome || p.campo_nome}</div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: '#888', marginTop: '2px' }}>{fmtDataEstesa(p.data.slice(0, 10))} · {p.ora_inizio}–{p.ora_fine} · {p.codice}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '10px', fontWeight: 800, letterSpacing: '1px', padding: '4px 10px', borderRadius: '20px', color: p.stato === 'CONFERMATA' ? '#22C55E' : '#EF4444', background: p.stato === 'CONFERMATA' ? '#22C55E20' : '#EF444420' }}>{p.stato}</span>
                {p.stato === 'CONFERMATA' && <button onClick={() => annulla(p.id)} style={{ background: '#EF444420', border: '1px solid #EF444440', color: '#f1948a', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 700 }}>Annulla</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'cal' && (
        <div style={s.card}>
          <div style={s.navBar}>
            <button style={s.navBtn} onClick={() => navSett(-1)}><ChevronLeft size={16}/></button>
            <div style={s.weekLabel}>{cal ? `${fmtLabel(cal.giorni[0].data)} – ${fmtLabel(cal.giorni[6].data)}` : '—'}</div>
            <button style={s.navBtn} onClick={() => navSett(1)}><ChevronRight size={16}/></button>
          </div>

          <div style={s.legenda}>
            <span><i style={s.legDot('#F4600C20', '#F4600C60')}/>Disponibile</span>
            <span><i style={s.legDot('#fff', '#fff')}/>Selezionato</span>
            <span><i style={s.legDot('#1c1c1c', '#2e2e2e')}/>Occupato</span>
            <span><i style={s.legDot('#EF444420', '#EF444460')}/>Bloccato</span>
          </div>

          <div style={{ opacity: loading && cal ? 0.5 : 1, transition: 'opacity 0.12s' }}>
            {!cal && loading && <div style={{ color: '#666', padding: '30px', textAlign: 'center' }}>Caricamento...</div>}
            {cal && cal.sedi.length === 0 && <div style={{ color: '#666', padding: '30px', textAlign: 'center' }}>Nessuna sede configurata.</div>}

            {cal && cal.sedi.map(sede => (
              <div key={sede.id}>
                <div style={s.sedeTitle}><MapPin size={14}/> {sede.nome}</div>
                {sede.campi.length === 0 && <div style={{ color: '#555', fontSize: 'var(--font-size-xs)', marginBottom: '10px' }}>Nessun campo configurato in questa sede.</div>}
                {sede.campi.map(campo => (
                  <div key={campo.id}>
                    <div style={s.campoTitle}>{campo.nome}</div>
                    {campo.slot.length === 0 && <div style={{ color: '#555', fontSize: 'var(--font-size-xs)', marginBottom: '10px' }}>Nessuno slot configurato.</div>}
                    {campo.slot.length > 0 && (
                      <table style={s.table}>
                        <thead>
                          <tr>
                            <th style={s.th}></th>
                            {cal.giorni.map(g => <th key={g.data} style={{ ...s.th, color: g.oggi ? '#F4600C' : '#666' }}>{fmtLabel(g.data)}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {campo.slot.map(slot => (
                            <tr key={slot.id}>
                              <td style={s.rowLabel}>{slot.ora_inizio}<br/><span style={{ color: 'var(--text-dim)' }}>{oraFine(slot.ora_inizio, slot.durata_minuti)}</span></td>
                              {slot.giorni.map(g => {
                                const isSel = selezione && selezione.slot.id === slot.id && selezione.data === g.data;
                                let style = { ...s.cellBase, background: 'transparent', color: 'var(--text-dim)', cursor: 'default' };
                                let content = '—';
                                if (g.disponibile) { style = { ...style, background: '#F4600C18', border: '1.5px solid #F4600C55', color: '#ff9a4d' }; content = 'Libero'; }
                                if (isSel) { style = { ...style, background: '#fff', border: '1.5px solid #fff', color: '#111' }; content = 'Selez.'; }
                                if (g.stato === 'CONFERMATA') {
                                  style = { ...style, background: g.mia ? '#F4600C30' : 'var(--bg-input)', border: `1px solid ${g.mia ? '#F4600C80' : 'var(--border2)'}`, color: g.mia ? '#ff9a4d' : '#666' };
                                  content = g.nome
                                    ? (isStaff || g.mia
                                        ? <>{g.nome.split(' ')[0]}<br/><span style={{ fontSize: '8px', opacity: 0.8 }}>{g.codice}</span></>
                                        : g.nome.split(' ')[0])
                                    : 'Occupato';
                                }
                                if (g.stato === 'BLOCCATA') {
                                  style = { ...style, background: '#EF444418', border: '1px solid #EF444450', color: '#f1948a' };
                                  content = isStaff && g.note ? g.note.slice(0, 14) : 'Bloccato';
                                }
                                const clickable = g.disponibile || (isStaff && (g.stato === 'CONFERMATA' || g.stato === 'BLOCCATA'));
                                return (
                                  <td key={g.data} style={s.cellTd}>
                                    <button style={{ ...style, cursor: clickable ? 'pointer' : 'default' }} disabled={!clickable} onClick={() => clickCella(sede, campo, slot, g)}>{content}</button>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {selezione && (
            <div style={{ marginTop: '14px', background: '#F4600C', borderRadius: '8px', padding: '12px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, fontFamily: "'Barlow Condensed'", fontWeight: 800, letterSpacing: '0.5px', color: '#fff', fontSize: 'var(--font-size-sm)' }}>
                  {selezione.sedeNome} — {selezione.campoNome} · {fmtDataEstesa(selezione.data)} · {selezione.ora_inizio}
                </div>
                {!bloccoAttivo && (
                  <>
                    {isStaff && <button onClick={() => setBloccoAttivo(true)} style={{ background: 'transparent', border: '1.5px solid #fff', color: '#fff', borderRadius: '6px', padding: '9px 16px', fontWeight: 800, cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>Segna occupato</button>}
                    <button onClick={vaiAlForm} style={{ background: '#fff', color: '#cc5500', border: 'none', borderRadius: '6px', padding: '9px 20px', fontWeight: 800, cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>Prenota →</button>
                  </>
                )}
              </div>
              {bloccoAttivo && (
                <div style={{ marginTop: '12px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <input autoFocus style={{ ...inp, flex: 1, minWidth: '200px' }} placeholder="Perché è occupato? (es. manutenzione, torneo, uso interno...)" value={bloccoNota} onChange={e => setBloccoNota(e.target.value)} />
                  <button onClick={() => setBloccoAttivo(false)} style={{ background: 'transparent', border: '1px solid #fff', color: '#fff', borderRadius: '6px', padding: '9px 14px', fontWeight: 700, cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>Annulla</button>
                  <button onClick={confermaBlocco} disabled={saving} style={{ background: '#fff', color: '#cc5500', border: 'none', borderRadius: '6px', padding: '9px 20px', fontWeight: 800, cursor: 'pointer', fontSize: 'var(--font-size-sm)', opacity: saving ? 0.6 : 1 }}>Conferma</button>
                </div>
              )}
              {err && <div style={{ marginTop: '10px', color: '#fff', fontWeight: 700, fontSize: 'var(--font-size-sm)' }}>{err}</div>}
            </div>
          )}

          {dettaglio && (
            <div style={{ marginTop: '14px', background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div style={{ fontFamily: "'Barlow Condensed'", fontWeight: 800, letterSpacing: '0.5px', color: 'var(--text)', fontSize: 'var(--font-size-sm)' }}>
                  {dettaglio.sedeNome} — {dettaglio.campoNome} · {fmtDataEstesa(dettaglio.data)} · {dettaglio.ora_inizio}–{dettaglio.ora_fine}
                </div>
                <button onClick={() => setDettaglio(null)} style={{ background: 'transparent', border: 'none', color: '#666', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>✕</button>
              </div>
              {dettaglio.stato === 'BLOCCATA' ? (
                <>
                  <div style={{ color: '#f1948a', fontSize: 'var(--font-size-sm)', marginBottom: '12px' }}>Occupato{dettaglio.note ? ` — ${dettaglio.note}` : ''}</div>
                  <button onClick={sbloccaDettaglio} style={{ background: '#22C55E20', border: '1px solid #22C55E40', color: '#4ade80', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontWeight: 700, fontSize: 'var(--font-size-xs)' }}>Libera slot</button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)', marginBottom: '10px' }}>
                    <strong>{dettaglio.nome}</strong> · {dettaglio.email} {dettaglio.telefono ? `· ${dettaglio.telefono}` : ''}<br/>
                    Codice <strong>{dettaglio.codice}</strong> · € {dettaglio.prezzo?.toFixed(2)}
                  </div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button onClick={togglePagatoDettaglio} style={{ background: dettaglio.pagato ? '#22C55E20' : 'var(--bg-input)', border: `1px solid ${dettaglio.pagato ? '#22C55E40' : 'var(--border2)'}`, color: dettaglio.pagato ? '#4ade80' : '#888', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontWeight: 700, fontSize: 'var(--font-size-xs)' }}>
                      {dettaglio.pagato ? 'Pagato ✓' : 'Segna come pagato'}
                    </button>
                    <button onClick={annullaDettaglio} style={{ background: '#EF444420', border: '1px solid #EF444440', color: '#f1948a', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontWeight: 700, fontSize: 'var(--font-size-xs)' }}>Annulla prenotazione</button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
